import { IGridifyQuery } from "models/common";
import { ApiResult } from "../shared/networking/api_result";
import HttpService from "../shared/networking/http_service";
import { getConfig } from "./config.service";
import { buildGridifyQueryString } from "shared/utils/gridify";
import {
  IKnittingProgramLibrary,
  IKnittingProgramLibraryDetails,
  IKnittingProgramLibraryResponse,
  IKnittingProgramLibraryVersion,
  ICreateKnittingProgramLibraryCommand,
  IUpdateKnittingProgramLibraryCommand,
  IRevertCommand,
  IRevertResult,
  IKnittingProgramLibraryDeleteReadiness,
} from "models/knittingProgramLibrary";

class KnittingProgramLibraryServiceAPI extends HttpService {
  public async getLibraries(
    mainProductLine: string,
    query?: IGridifyQuery,
  ): Promise<ApiResult<IKnittingProgramLibraryResponse>> {
    const params = new URLSearchParams(buildGridifyQueryString(query).slice(1));
    params.append("mainProductLine", mainProductLine);
    return this.get<IKnittingProgramLibraryResponse>(
      `/mdsapi/knittingProgramLibraries/list?${params.toString()}`,
    );
  }

  public async getLibrary(
    id: string,
  ): Promise<ApiResult<IKnittingProgramLibraryDetails>> {
    return this.get<IKnittingProgramLibraryDetails>(
      `/mdsapi/knittingProgramLibraries/${encodeURIComponent(id)}`,
    );
  }

  public async createLibrary(
    command: ICreateKnittingProgramLibraryCommand,
  ): Promise<ApiResult<string>> {
    return this.postDataForData<ICreateKnittingProgramLibraryCommand, string>(
      `/mdsapi/knittingProgramLibraries`,
      command,
    );
  }

  public async updateLibrary(
    id: string,
    command: IUpdateKnittingProgramLibraryCommand,
  ): Promise<ApiResult<void>> {
    return this.putForData<void>(
      `/mdsapi/knittingProgramLibraries/${encodeURIComponent(id)}/edit`,
      command,
    );
  }

  public async deleteLibrary(id: string): Promise<ApiResult<void>> {
    return this.delete(
      `/mdsapi/knittingProgramLibraries/${encodeURIComponent(id)}`,
    );
  }

  public async getDeleteReadiness(
    id: string,
  ): Promise<ApiResult<IKnittingProgramLibraryDeleteReadiness>> {
    return this.get<IKnittingProgramLibraryDeleteReadiness>(
      `/mdsapi/knittingProgramLibraries/${encodeURIComponent(id)}/delete-readiness`,
    );
  }

  public async transferToDefaultLibrary(id: string): Promise<ApiResult<void>> {
    return this.postDataForData<void, void>(
      `/mdsapi/knittingProgramLibraries/${encodeURIComponent(id)}/transfer-to-default-library`,
      undefined,
      null,
    );
  }

  public async getLibraryHistory(
    id: string,
  ): Promise<ApiResult<IKnittingProgramLibraryVersion[]>> {
    return this.get<IKnittingProgramLibraryVersion[]>(
      `/mdsapi/knittingProgramLibraries/${encodeURIComponent(id)}/history`,
    );
  }

  public async revertLibrary(
    id: string,
    command: IRevertCommand,
  ): Promise<ApiResult<IRevertResult>> {
    return this.postDataForData<IRevertCommand, IRevertResult>(
      `/mdsapi/knittingProgramLibraries/${encodeURIComponent(id)}/revert`,
      command,
      null,
    );
  }

  public downloadLibrary(id: string): string {
    return `/mdsapi/knittingProgramLibraries/${encodeURIComponent(id)}/download`;
  }
}

const appConfig = getConfig();
const TimeoutMilliseconds = Number(appConfig.httpTimeoutMs);
const KnittingProgramLibraryService = new KnittingProgramLibraryServiceAPI(
  TimeoutMilliseconds,
);

export default KnittingProgramLibraryService;
